import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TableViewer from '../components/TableViewer';

const TableViewerPage: React.FC = () => {
  const location = useLocation();
  const [tableData, setTableData] = useState<any>(null);
  const [endpoint, setEndpoint] = useState<string>('');
  const [timestamp, setTimestamp] = useState<string>('');

  useEffect(() => {
    
    // FIRST: Try to get data from URL parameter (fastest method)
    const urlParams = new URLSearchParams(window.location.search);
    const dataKey = urlParams.get('dataKey');
    
    if (dataKey) {
      try {
        const storedData = localStorage.getItem(dataKey);
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setTableData(parsedData.data);
          setEndpoint(parsedData.endpoint || '');
          setTimestamp(parsedData.timestamp || '');
          // Clean up after use
          localStorage.removeItem(dataKey);
          return;
        }
      } catch (error) {
        console.error('Error loading data from URL parameter:', error);
      }
    }

    // If no dataKey found, there's nothing we can do - wait for postMessage
    
    // THIRD: Try to get data from location state
    if (location.state) {
      const { data, endpoint: ep, timestamp: ts } = location.state;
      setTableData(data);
      setEndpoint(ep || '');
      setTimestamp(ts || '');
      return;
    }

    // Listen for postMessage from parent window
    const handleMessage = (event: MessageEvent) => {
      
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return;
      }
      
      if (event.data && event.data.data) {
        setTableData(event.data.data);
        setEndpoint(event.data.endpoint || '');
        setTimestamp(event.data.timestamp || '');
        
        // Clean up localStorage if a key was provided
        if (event.data.storageKey) {
          try {
            localStorage.removeItem(event.data.storageKey);
          } catch (error) {
            console.error('Error cleaning up localStorage:', error);
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Send ready message to parent window multiple times to ensure it's received
    const sendReadyMessage = () => {
      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage({ type: 'table-viewer-ready' }, window.location.origin);
        } catch (error) {
          console.error('Error sending message to parent:', error);
        }
      } else {
      }
    };

    // Send immediately
    sendReadyMessage();
    
    // Send again after small delays to handle timing issues
    const timeouts = [100, 300, 500, 1000, 1500, 2000, 3000].map(delay => 
      setTimeout(sendReadyMessage, delay)
    );

    // Also try to request data directly if no response after 4 seconds
    const fallbackTimeout = setTimeout(() => {
      if (!tableData) {
        
        // Try to get the specific dataKey from URL again as fallback
        const urlParams = new URLSearchParams(window.location.search);
        const dataKey = urlParams.get('dataKey');
        
        if (dataKey) {
          try {
            const storedData = localStorage.getItem(dataKey);
            if (storedData) {
              const parsedData = JSON.parse(storedData);
              setTableData(parsedData.data);
              setEndpoint(parsedData.endpoint || '');
              setTimestamp(parsedData.timestamp || '');
              // Don't clean up here in case we need it for retries
              return;
            } else {
            }
          } catch (error) {
            console.error('Fallback: Error loading data for dataKey:', dataKey, error);
          }
        }
        
        // If localStorage fallback didn't work, try requesting from parent
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage({ type: 'table-viewer-request-data' }, window.location.origin);
          } catch (error) {
            console.error('Error requesting data from parent:', error);
          }
        }
      }
    }, 4000);

    return () => {
      window.removeEventListener('message', handleMessage);
      // Clean up timeouts
      timeouts.forEach(timeout => clearTimeout(timeout));
      clearTimeout(fallbackTimeout);
    };
  }, [location.state, tableData]);

  if (!tableData) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
        color: 'white'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '400px',
          margin: '2rem auto'
        }}>
          <h2 style={{ margin: '0 0 1rem 0' }}>📊 Table Viewer</h2>
          <p style={{ margin: '0 0 2rem 0', opacity: 0.9 }}>Loading table data...</p>
          <div style={{ margin: '2rem 0' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid rgba(255,255,255,0.3)',
              borderTop: '4px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
          </div>
          <button 
            onClick={() => window.close()} 
            style={{ 
              padding: '0.75rem 1.5rem', 
              background: 'rgba(255,255,255,0.2)', 
              color: 'white', 
              border: '1px solid rgba(255,255,255,0.3)', 
              borderRadius: '6px', 
              cursor: 'pointer',
              fontSize: '0.9rem',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
            }}
          >
            Close Window
          </button>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return <TableViewer data={tableData} endpoint={endpoint} timestamp={timestamp} />;
};

export default TableViewerPage;
